import { useState, useRef, useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X, Search, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useParentLineSearch,
  useAssignChildren,
  useReassignChildren,
  useRemoveChildren,
} from '@/hooks/usePIAB'
import { toast } from '@/stores/useToastStore'
import type { ParentLineItemInfo } from '@/types/dag'

interface AssignParentSkuDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  childRowId: string
  childSkuNumber: string | null
  existingParent: ParentLineItemInfo | null
}

export function AssignParentSkuDrawer({
  open,
  onOpenChange,
  planId,
  versionId,
  childRowId,
  childSkuNumber,
  existingParent,
}: AssignParentSkuDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedParent, setSelectedParent] = useState<{
    id: string
    skuNumber: string | null
    description: string | null
  } | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: searchData, isLoading: isSearching } = useParentLineSearch(
    planId,
    versionId,
    searchQuery
  )

  const assignMutation = useAssignChildren(planId, versionId)
  const reassignMutation = useReassignChildren(planId, versionId)
  const removeMutation = useRemoveChildren(planId, versionId)

  const parentRows = searchData?.rows ?? []

  // Reset state and invalidate cached parent list when drawer opens
  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setSelectedParent(
        existingParent
          ? {
              id: existingParent.id,
              skuNumber: existingParent.sku_number ?? null,
              description: existingParent.description ?? null,
            }
          : null
      )
      setShowDropdown(false)
      setRemoveConfirmOpen(false)
      queryClient.removeQueries({
        queryKey: ['parent-line-search', planId, versionId],
      })
    }
  }, [open, queryClient, planId, versionId, existingParent])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedParent(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    setShowDropdown(true)
  }

  const handleSelectParent = useCallback(
    (row: { id: string; cells: Record<string, unknown> }) => {
      setSelectedParent({
        id: row.id,
        skuNumber: (row.cells['plan_sku_num'] as string) ?? null,
        description: (row.cells['description'] as string) ?? null,
      })
      setSearchQuery(
        String(row.cells['plan_sku_num'] ?? row.cells['description'] ?? '')
      )
      setShowDropdown(false)
    },
    []
  )

  const handleSave = useCallback(() => {
    if (!selectedParent) return
    const mutation = existingParent ? reassignMutation : assignMutation
    mutation.mutate(
      { lineId: selectedParent.id, childLineItemIds: [childRowId] },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }, [
    selectedParent,
    existingParent,
    assignMutation,
    reassignMutation,
    childRowId,
    onOpenChange,
  ])

  const handleRemoveParent = useCallback(() => {
    if (!existingParent) return
    removeMutation.mutate(
      { lineId: existingParent.id, childLineItemIds: [childRowId] },
      {
        onSuccess: () => {
          toast.success('Parent SKU removed successfully')
          setRemoveConfirmOpen(false)
          onOpenChange(false)
        },
      }
    )
  }, [existingParent, removeMutation, childRowId, onOpenChange])

  const shouldShowDropdown = showDropdown
  const isSaving = assignMutation.isPending || reassignMutation.isPending

  return (
    <>
      <Sheet open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
        <SheetContent
          side="right"
          className="w-[480px] sm:max-w-[480px] flex flex-col"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              Assign Parent SKU
              {childSkuNumber && (
                <Badge variant="secondary">{childSkuNumber}</Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              Search and select a parent SKU to assign to this child line item.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Search bar — hidden when a parent is selected */}
            {!selectedParent && (
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    placeholder="Search for parent SKU..."
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    className="pl-9"
                  />
                </div>

                {/* Search results dropdown */}
                {shouldShowDropdown && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-lg max-h-[240px] overflow-auto">
                    {isSearching ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : parentRows.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        No parent SKUs found
                      </div>
                    ) : (
                      <div className="px-2 py-1.5">
                        <p className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                          Suggestions
                        </p>
                        {parentRows.map(row => (
                          <div
                            key={row.id}
                            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                            onClick={() => handleSelectParent(row)}
                          >
                            <p className="flex-1 text-sm font-medium text-muted-foreground truncate">
                              SKU #{String(row.cells['plan_sku_num'] ?? '')}
                            </p>
                            <p className="text-xs font-medium text-muted-foreground truncate">
                              {String(row.cells['description'] ?? '')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected parent card */}
            {selectedParent && (
              <div className="rounded-md border border-border p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">
                    SKU #{selectedParent.skuNumber ?? 'No SKU'}
                  </p>
                  {selectedParent.description && (
                    <p className="text-xs text-muted-foreground">
                      {selectedParent.description}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 cursor-pointer"
                  onClick={() => {
                    setSelectedParent(null)
                    setSearchQuery('')
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <SheetFooter className="flex-row flex-shrink-0 items-center justify-end gap-2.5 border-t border-[#D6DAE1] px-6 py-6">
            {existingParent && (
              <button
                type="button"
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={isSaving || removeMutation.isPending}
                className="mr-auto text-sm text-destructive hover:text-destructive/80 underline cursor-pointer disabled:opacity-50"
              >
                Remove Parent
              </button>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!selectedParent || isSaving}
              className="cursor-pointer"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save Parent SKU'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        title="Remove Parent SKU"
        description="Are you sure you want to remove the parent SKU assignment? This will unlink this child from its parent."
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleRemoveParent}
        variant="destructive"
        isLoading={removeMutation.isPending}
      />
    </>
  )
}
